import { Component, OnInit, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { AuthService } from '../auth.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  email: string = '';
  password: string = '';
  confirmPassword: string = '';
  isDarkMode = true;
  showPasswordModal = false;
  isNewUser = false;
  requiresPassword = false;

  constructor(
    private router: Router,
    private msalService: MsalService,
    private authService: AuthService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) { }

  ngOnInit() {
    // Detectar el tema actual
    this.updateTheme();
    
    // Observar cambios en el tema
    const observer = new MutationObserver(() => {
      this.updateTheme();
    });
    
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Suscribirse a eventos de MSAL para detectar cuando el login es exitoso
    this.msalService.instance.addEventCallback((event: any) => {
      if (event.eventType === 'msal:loginSuccess' || event.eventType === 'msal:acquireTokenSuccess') {
        // Si el login fue exitoso, redirigir a la página principal
        if (this.authService.isAuthenticated()) {
          this.router.navigate(['/home']);
        }
      }
    });

    // Verificar si el usuario ya está autenticado (en caso de que regrese después del redirect)
    // Esperar un momento para que el AppComponent procese el redirect primero
    setTimeout(() => {
      this.checkAndRedirect();
    }, 100);

    // También verificar periódicamente en caso de que la autenticación se complete después
    const checkInterval = setInterval(() => {
      this.checkAndRedirect();
    }, 300);

    // Limpiar el intervalo después de 5 segundos
    setTimeout(() => {
      clearInterval(checkInterval);
    }, 5000);
  }

  private checkAndRedirect() {
    // Verificar si el usuario ya está autenticado
    if (this.authService.isAuthenticated()) {
      // Si está autenticado, redirigir a la página principal
      this.router.navigate(['/home']);
    }
  }

  private updateTheme() {
    this.isDarkMode = document.body.classList.contains('dark-theme');
  }

  loginWithEmail() {
    if (!this.email || !this.email.trim()) {
      alert('Por favor, ingresa un email válido');
      return;
    }

    // Validar formato de email básico
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.email)) {
      alert('Por favor, ingresa un email válido');
      return;
    }

    // Si el modal de contraseña está abierto, procesar la contraseña
    if (this.showPasswordModal) {
      this.submitPassword();
      return;
    }

    // Autenticar con email (sin contraseña primero para verificar si existe)
    this.authService.loginWithEmail(this.email).subscribe({
      next: (result) => {
        if (result.error) {
          console.error('Error en login con email:', result.error);
          alert(result.error || 'Error al autenticar. Por favor, intenta nuevamente.');
          return;
        }

        // Si se requiere contraseña, mostrar el modal
        if (result.requiresPassword) {
          this.isNewUser = result.isNewUser || false;
          this.requiresPassword = true;
          this.showPasswordModal = true;
          return;
        }

        // Si el usuario fue creado o actualizado exitosamente
        if (result.action === 'created' || result.action === 'updated' || result.action === 'password_created') {
          this.completeLogin(result);
        } else {
          alert('Error al autenticar. Por favor, intenta nuevamente.');
        }
      },
      error: (error) => {
        console.error('Error en la petición de login con email:', error);
        const errorMessage = error.error?.error || 'Error al conectar con el servidor. Por favor, intenta nuevamente.';
        alert(errorMessage);
      }
    });
  }

  submitPassword() {
    if (!this.password || this.password.length < 6) {
      alert('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    // Si es nuevo usuario, verificar confirmación
    if (this.isNewUser && this.password !== this.confirmPassword) {
      alert('Las contraseñas no coinciden');
      return;
    }

    // Enviar email y contraseña
    this.authService.loginWithEmail(this.email, this.password, this.isNewUser).subscribe({
      next: (result) => {
        if (result.error) {
          console.error('Error al crear/verificar contraseña:', result.error);
          alert(result.error || 'Error al procesar la contraseña. Por favor, intenta nuevamente.');
          return;
        }

        // Contraseña creada o verificada exitosamente
        if (result.action === 'created' || result.action === 'updated' || result.action === 'password_created') {
          this.closePasswordModal();
          this.completeLogin(result);
        } else {
          alert('Error al procesar la contraseña. Por favor, intenta nuevamente.');
        }
      },
      error: (error) => {
        console.error('Error en la petición de contraseña:', error);
        const errorMessage = error.error?.error || 'Error al conectar con el servidor. Por favor, intenta nuevamente.';
        alert(errorMessage);
      }
    });
  }

  closePasswordModal() {
    this.showPasswordModal = false;
    this.password = '';
    this.confirmPassword = '';
    this.isNewUser = false;
    this.requiresPassword = false;
  }

  private completeLogin(result: any) {
    // Guardar información de autenticación en localStorage
    const emailNormalized = this.email.toLowerCase().trim();
    localStorage.setItem('userEmail', emailNormalized);
    localStorage.setItem('userName', result.name || emailNormalized);
    localStorage.setItem('userId', result.userId || this.generateUserId());
    localStorage.setItem('authMethod', 'email');

    // Forzar recarga del estado de autenticación en el AppComponent
    setTimeout(() => {
      this.router.navigate(['/home']).then(() => {
        // También podemos forzar un recarga de la página si es necesario
        // window.location.reload(); // Descomentar si sigue sin funcionar
      });
    }, 100);
  }

  /**
   * Genera un ID único para el usuario
   */
  private generateUserId(): string {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 9);
    return `email_${timestamp}_${randomStr}`;
  }

  loginWithGoogle() {
    // TODO: Implementar login con Google
    console.log('Login con Google');
  }

  loginWithMicrosoft() {
    // Usar la autenticación de Microsoft existente
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.msalService.loginRedirect({
        scopes: ['user.read']
      });
    }
  }

  goBack() {
    this.router.navigate(['/home']);
  }
}

